import {
  AlertComponent,
  AttachedDesignUploadNotice,
  BasicInfoComponent,
  CombinedChart,
  FormSection,
  InputExcelFile,
  ModalComponent,
  NavBar,
  PdfProposalFile,
  ProposalDescription,
  SolutionComponent,
  TallyTable,
  UploadFileForm,
  VSvsTVDChart,
  WBDDesign,
  Wellbore3d,
} from "../../components";
import {
  useAttachedDesignUpload,
  useFetch,
  usePermissions,
  useUploadedDocumentDownload,
} from "../../hooks";
import {
  ClientProps,
  DocumentProps,
  ProductProps,
} from "../../interfaces/interfaces";
import styles from "../main.module.sass";
import { Box, Container, Fade } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import DataContext from "../../context/DataContext";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { PDFView } from "../../components/pdf";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useScreenshot } from "usescreenshot-react";
import SellIcon from "@mui/icons-material/Sell";
import SimulatorContext from "../../context/SimulatorContext";
import { initial_prod_chart } from "../../utils/data";
import { useDate } from "../../hooks/useDate";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import ScienceIcon from "@mui/icons-material/Science";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import BuildIcon from "@mui/icons-material/Build";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";

interface TechProposalInfoProps {
  clients: ClientProps[];
  products: ProductProps[];
  customTools: ProductProps[];
}

interface FetchResponse {
  success?: boolean;
  data: TechProposalInfoProps;
}

export const TechDesignForm = () => {
  const { idTech } = useParams();
  let [searchParams] = useSearchParams();
  const { getDateFromString } = useDate();
  const pdf = searchParams.get("pdf");
  const { takeScreenshot } = useScreenshot();
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useFetch<FetchResponse>("tech-proposal");
  const [inEdit, setInEdit] = useState(false);
  const [inPdf, setInPdf] = useState(false);
  const [isThereProdImage, setIsThereProdImage] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const navigate = useNavigate();
  const { canManageDesigns } = usePermissions();
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [customTools, setCustomTools] = useState<ProductProps[]>([]);
  const savedProposalId = idTech && idTech !== "0" ? parseInt(idTech) : null;

  const {
    fetchDataClient,
    toSave,
    onSaveProp,
    onEditprop,
    onUpdateBasicInfo,
    onCancelEdition,
    onCreatePdf,
    fetchDataTechProp,
    fetchDataProducts,
    onResetValues,
    isSuccess,
    isError,
    data: dataProposal,
  } = useContext(DataContext);

  const { fetchTechSolution } = useContext(SimulatorContext);
  const { attachedUpload, refreshAttachedUpload } = useAttachedDesignUpload({
    endpointBase: "proposal-list",
    designId: savedProposalId,
    designIdField: "proposalId",
    wellId: dataProposal.basicInfo.well?.id,
    enabled: Boolean(savedProposalId),
  });
  const { downloadUploadedDocument, isDownloadingUploadedDocument } =
    useUploadedDocumentDownload("pdf-proposal-adm");

  useEffect(() => {
    fetchDataClient();
    if (idTech && idTech !== "0") {
      fetchDataTechProp(parseInt(idTech));
      fetchTechSolution(parseInt(idTech));
      setInEdit(true);
      dataProposal.basicInfo.prodImage && setIsThereProdImage(true);
      if (pdf) setInPdf(true);
    } else {
      setInEdit(false);
      setInPdf(false);
      onResetValues();
    }

    return () => {};
  }, [idTech]);

  useEffect(() => {
    fetchDataProducts();
  }, []);

  useEffect(() => {
    if (data) {
      const products = data ? data.data.products : [];
      const customTools = data ? data.data.customTools : [];
      setProducts(products);
      setCustomTools(customTools);
    }
  }, [data]);

  useEffect(() => {
    setTimeout(() => {
      setIsUploadSuccess(false);
      setIsUploadError(false);
    }, 4000);
  }, [isUploadSuccess, isUploadError]);

  const handleUploadProposal = async ({
    client,
    well,
    customName,
    file,
    installationDate,
    proposalId,
  }: DocumentProps) => {
    try {
      setIsUploadingDocument(true);
      setIsUploadModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      formData.append("customName", customName || dataProposal.basicInfo.customName);
      installationDate &&
        formData.append("installationDate", installationDate.toISOString());
      proposalId && formData.append("proposalId", String(proposalId));
      formData.append("type", "proposal");
      formData.append("file", file, file.name);
      const response = await fetch(
        process.env.REACT_APP_DEV_API + "/proposal-upload",
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        },
      );
      const res = await response.json();
      if (res.success) {
        setIsUploadSuccess(true);
        refreshAttachedUpload();
      } else setIsUploadError(true);
    } catch {
      setIsUploadError(true);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const buttons = [
    {
      title: "Cancel",
      action: onCancelEdition,
      icon: <CancelIcon />,
      disabled: !!inPdf,
    },
    ...(canManageDesigns
      ? [
          {
            title: "Order",
            action: () => navigate(`/sales/tech/${idTech}`),
            icon: <SellIcon />,
            disabled: !toSave,
          },
          {
            title: "Save",
            action: inEdit ? () => idTech && onEditprop(idTech) : onSaveProp,
            icon: <SaveIcon />,
            disabled: !toSave,
          },
          {
            title: "Upload",
            action: () => setIsUploadModalOpen(true),
            icon: <DriveFolderUploadIcon />,
            disabled:
              !dataProposal.basicInfo.client?.id || !dataProposal.basicInfo.well?.id,
          },
        ]
      : []),
    {
      title: !inPdf ? "To Pdf" : "Go Back",
      action: () => {
        onCreatePdf();
        setInPdf(!inPdf);
      },
      icon: !inPdf ? <PictureAsPdfIcon /> : <ArrowBackIosIcon />,
    },
  ];

  const handleReadExcelFile = (data: any) => {
    let labels = [];
    let dataset1 = [],
      dataset2 = [],
      dataset3 = [];
    for (let i = 1; i < data.length; i++) {
      labels.push(getDateFromString(data[i][0]));
      dataset1.push(data[i][1]);
      dataset2.push(data[i][2]);
      dataset3.push(data[i][3]);
    }
    setIsThereProdImage(true);
    onUpdateBasicInfo("prodChartData", {
      labels,
      dataset1,
      dataset2,
      dataset3,
    });
  };

  const handleRemoveImage = () => {
    onUpdateBasicInfo("prodChartData", initial_prod_chart);
    onUpdateBasicInfo("prodImage", "");
    setIsThereProdImage(false);
  };

  const getImage = () => {
    if (!ref.current) {
      return;
    }
    takeScreenshot(ref.current, {
      backgroundColor: null,
      logging: false,
    })
      .then((res) => onUpdateBasicInfo("prodImage", res))
      .catch(console.log);
  };

  return (
    <>
      <NavBar title="Technical Proposal" buttons={buttons} />

      {inPdf ? (
        <Box sx={{ marginTop: "80px" }}>
          <PDFView
            fileName={`TECHNICAL PROPOSAL - ${dataProposal.basicInfo.well.name}`}
            document={(us) => (
              <PdfProposalFile
                data={dataProposal}
                unitSystem={us}
                tallyUnitSystem={dataProposal.tallyUnitSystem}
              />
            )}
            action={pdf ? onCreatePdf : () => {}}
          />
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: "80px",
            minHeight: "calc(100vh - 80px)",
            background:
              "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
            padding: { xs: 2, md: 4 },
            overflowX: "hidden",
          }}
        >
          <Container maxWidth="xl">
            <Fade in={true} timeout={600}>
              <Box>
                <AttachedDesignUploadNotice
                  designLabel="technical proposal"
                  isDownloading={isDownloadingUploadedDocument}
                  upload={attachedUpload}
                  onDownload={() =>
                    attachedUpload &&
                    downloadUploadedDocument(
                      attachedUpload.path,
                      attachedUpload.customName,
                    )
                  }
                  onReplace={() => setIsUploadModalOpen(true)}
                />

                <FormSection title="Well Information" icon={<InfoOutlinedIcon />}>
                  <BasicInfoComponent />
                </FormSection>

                <FormSection
                  title="Vertical Section vs True Vertical Depth"
                  icon={<ShowChartIcon />}
                  defaultExpanded={false}
                >
                  <VSvsTVDChart />
                </FormSection>

                <FormSection title="Production History" icon={<BarChartIcon />}>
                  <InputExcelFile
                    onReadExcelData={handleReadExcelFile}
                    onGetImage={getImage}
                    disabled={!isThereProdImage}
                    onRemoveImage={handleRemoveImage}
                  />
                  <div ref={ref} className={styles.center}>
                    <CombinedChart
                      prodChartData={dataProposal.basicInfo.prodChartData}
                    />
                  </div>
                </FormSection>

                <FormSection title="Simulators" icon={<ScienceIcon />}>
                  <SolutionComponent />
                </FormSection>

                <FormSection
                  title="3D Wellbore"
                  icon={<ViewInArIcon />}
                  defaultExpanded={false}
                >
                  <Wellbore3d />
                </FormSection>

                <FormSection
                  title="Tool Description"
                  icon={<BuildIcon />}
                  defaultExpanded={false}
                >
                  <ProposalDescription />
                </FormSection>

                <FormSection title="Tally Design" icon={<ListAltIcon />}>
                  <TallyTable products={products} customTools={customTools} />
                </FormSection>

                <FormSection title="Wellbore Design" icon={<AccountTreeIcon />}>
                  <WBDDesign />
                </FormSection>
              </Box>
            </Fade>
          </Container>
        </Box>
      )}

      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isUploadSuccess && <AlertComponent type="success" />}
      {isUploadError && <AlertComponent type="error" />}
      {isUploadingDocument && <div className={styles.backdrop}></div>}
      {isUploadModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={[dataProposal.basicInfo.client]}
              wellOptions={[dataProposal.basicInfo.well]}
              type="proposal"
              fetchDataWells={() => {}}
              initialValues={{
                client: dataProposal.basicInfo.client,
                well: dataProposal.basicInfo.well,
                customName: dataProposal.basicInfo.customName,
                proposalId: savedProposalId,
              }}
              lockClientWell
              onUploadFile={(values: DocumentProps) =>
                handleUploadProposal(values)
              }
              onCancel={() => setIsUploadModalOpen(false)}
            />
          }
        />
      )}
    </>
  );
};
